import React, { useState, useEffect, useCallback } from 'react';
import AiGroupDetail from './AiGroupDetail';
import { Cpu, Users, X, Crown, Eye, Trash2 } from 'lucide-react';
import api from '../api';

const SWIPE_THRESHOLD = -80;
const REFRESH_INTERVAL = 24 * 60 * 60 * 1000;

const GroupListModal = ({ isOpen, onClose, userId, userName, t, onRequestJoin, onOpenUserGroup }) => {
  const [activeTab, setActiveTab] = useState('silicon'); // 'silicon' | 'carbon'
  const [aiGroups, setAiGroups] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [selectedAIGroup, setSelectedAIGroup] = useState(null);
  const [selectedUserGroup, setSelectedUserGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [swipingId, setSwipingId] = useState(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [touchStart, setTouchStart] = useState(null);

  const getStorageKey = useCallback(() => `ai_groups_${userId}`, [userId]);

  const loadAiGroups = useCallback(async () => {
    if (!userId) return;

    try {
      const storageKey = getStorageKey();
      const stored = localStorage.getItem(storageKey);
      const now = Date.now();

      if (stored) {
        const { groups: storedGroups, deletedIds, timestamp } = JSON.parse(stored);

        if (now - timestamp < REFRESH_INTERVAL && storedGroups.length > 0) {
          setAiGroups(storedGroups);
          return;
        }

        try {
          const randomGroups = await api.getRandomAIGroups(deletedIds || [], 3);
          const newData = { groups: randomGroups, deletedIds: deletedIds || [], timestamp: now };
          localStorage.setItem(storageKey, JSON.stringify(newData));
          setAiGroups(randomGroups);
        } catch (apiErr) {
          console.error('API获取AI群聊失败，使用缓存数据:', apiErr);
          if (storedGroups && storedGroups.length > 0) {
            setAiGroups(storedGroups);
          } else {
            setAiGroups([]);
          }
        }
      } else {
        try {
          const randomGroups = await api.getRandomAIGroups([], 3);
          const newData = { groups: randomGroups, deletedIds: [], timestamp: now };
          localStorage.setItem(storageKey, JSON.stringify(newData));
          setAiGroups(randomGroups);
        } catch (apiErr) {
          console.error('API获取AI群聊失败:', apiErr);
          setAiGroups([]);
        }
      }
    } catch (err) {
      console.error('获取AI群聊列表失败:', err);
      setAiGroups([]);
    }
  }, [userId, getStorageKey]);

  const loadUserGroups = useCallback(async () => {
    if (!userId) return;

    try {
      const allGroups = await api.getAvailableGroups(userId);
      const userOnlyGroups = (allGroups || []).filter(g => g.type === 'user_group');
      setUserGroups(userOnlyGroups);
    } catch (err) {
      console.error('获取用户群聊列表失败:', err);
      setUserGroups([]);
    }
  }, [userId]);

  const loadAllGroups = useCallback(async () => {
    if (!isOpen || !userId) return;

    setLoading(true);
    setError(null);

    try {
      await Promise.all([loadAiGroups(), loadUserGroups()]);
    } catch (err) {
      console.error('加载群聊列表失败:', err);
    } finally {
      setLoading(false);
    }
  }, [isOpen, userId, loadAiGroups, loadUserGroups]);

  useEffect(() => {
    if (isOpen) {
      loadAllGroups();
    }
  }, [isOpen, loadAllGroups]);

  const handleDeleteAiGroup = async (groupId) => {
    const storageKey = getStorageKey();
    const stored = localStorage.getItem(storageKey);
    const now = Date.now();

    let deletedIds = [];
    let currentGroups = [];

    if (stored) {
      const parsed = JSON.parse(stored);
      deletedIds = parsed.deletedIds || [];
      currentGroups = parsed.groups || [];
    }

    if (!deletedIds.includes(groupId)) {
      deletedIds.push(groupId);
    }

    const remainingGroups = currentGroups.filter(g => g.id !== groupId);

    if (remainingGroups.length < 3) {
      try {
        const newGroups = await api.getRandomAIGroups(deletedIds, 3 - remainingGroups.length);
        const updatedGroups = [...remainingGroups, ...newGroups].slice(0, 3);
        const newData = { groups: updatedGroups, deletedIds, timestamp: now };
        localStorage.setItem(storageKey, JSON.stringify(newData));
        setAiGroups(updatedGroups);
      } catch (err) {
        console.error('获取新AI群聊失败:', err);
        const newData = { groups: remainingGroups, deletedIds, timestamp: now };
        localStorage.setItem(storageKey, JSON.stringify(newData));
        setAiGroups(remainingGroups);
      }
    } else {
      const newData = { groups: remainingGroups, deletedIds, timestamp: now };
      localStorage.setItem(storageKey, JSON.stringify(newData));
      setAiGroups(remainingGroups);
    }
  };

  const handleTouchStart = (e, groupId) => {
    if (activeTab !== 'silicon') return; // Only allow swipe on AI groups
    setSwipingId(groupId);
    setTouchStart(e.touches[0].clientX);
    setSwipeOffset(0);
  };

  const handleTouchMove = (e) => {
    if (activeTab !== 'silicon' || !touchStart) return;
    const diff = e.touches[0].clientX - touchStart;
    if (diff < 0) {
      setSwipeOffset(Math.max(diff, SWIPE_THRESHOLD));
    }
  };

  const handleTouchEnd = () => {
    if (activeTab !== 'silicon') return;

    if (swipeOffset <= SWIPE_THRESHOLD / 2) {
      if (swipingId) {
        handleDeleteAiGroup(swipingId);
      }
    }

    setSwipeOffset(0);
    setSwipingId(null);
    setTouchStart(null);
  };

  const handleApplyJoin = async (group, e) => {
    e.stopPropagation();
    if (loading) return;

    setLoading(true);
    setError(null);

    try {
      const owner = group.members?.find(m => m.role === 'owner');
      if (!owner) {
        setError('无法找到群主');
        setLoading(false);
        return;
      }

      const request = await api.requestJoinGroup(
        group.id,
        userId,
        userName || '匿名用户',
        owner.userId
      );

      if (onRequestJoin) {
        onRequestJoin(request);
      }

      onClose();
    } catch (err) {
      console.error('申请加入失败:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupClick = (group) => {
    if (group.type === 'ai_group') {
      setSelectedAIGroup(group);
    } else if (group.type === 'user_group') {
      const isMember = group.members?.some(m => m.userId === userId);
      if (isMember) {
        setSelectedUserGroup(group);
      } else {
        setError('你不是该群成员，无法进入');
      }
    }
  };

  const handleAIGroupClose = () => {
    setSelectedAIGroup(null);
  };

  const handleCreateUserGroup = (newGroup) => {
    setUserGroups(prev => [newGroup, ...prev]);
    // Switch to carbon tribe tab to show the new group
    setActiveTab('carbon');
  };

  if (selectedAIGroup) {
    return (
      <AiGroupDetail
        group={selectedAIGroup}
        isOwner={selectedAIGroup.ownerId === userId}
        onClose={handleAIGroupClose}
        t={t}
        userId={userId}
        userName={userName}
        onCreateUserGroup={handleCreateUserGroup}
        isReadOnly={true}
      />
    );
  }

  if (selectedUserGroup && onOpenUserGroup) {
    onOpenUserGroup(selectedUserGroup);
    setSelectedUserGroup(null);
    onClose();
    return null;
  }

  if (!isOpen) return null;

  const currentGroups = activeTab === 'silicon' ? aiGroups : userGroups;
  const isSiliconTab = activeTab === 'silicon';

  return (
    <div className="absolute inset-0 z-[80] bg-[#050510]/95 backdrop-blur-3xl flex flex-col page-enter">
      <div className="safe-top pt-6 px-6 flex justify-between items-center mb-4">
        <div className="flex-1">
          <h2 className="text-2xl font-black text-white mb-1 flex items-center gap-3 text-glow">
            {isSiliconTab ? (
              <><Cpu className="text-cyan-400" size={28}/> {t.aiMatrix}</>
            ) : (
              <><Users className="text-emerald-400" size={28}/> {t.carbonTribe}</>
            )}
          </h2>
          <p className="text-xs text-gray-400 font-mono tracking-widest ml-1">
            {isSiliconTab ? '' : ''}
          </p>
        </div>
        <button onClick={onClose} className="p-3 bg-white/10 rounded-full hover:bg-white/20">
          <X size={24} className="text-white" />
        </button>
      </div>

      {/* Tab Switcher */}
      <div className="mx-6 mb-4">
        <div className="flex rounded-2xl bg-white/5 p-1 border border-white/10">
          <button
            onClick={() => setActiveTab('silicon')}
            className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
              activeTab === 'silicon'
                ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-400 border border-cyan-500/30 shadow-[0_0_20px_rgba(34,211,238,0.2)]'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <Cpu size={18} />
            {t.aiMatrix}
          </button>
          <button
            onClick={() => setActiveTab('carbon')}
            className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
              activeTab === 'carbon'
                ? 'bg-gradient-to-r from-emerald-500/20 to-green-500/20 text-emerald-400 border border-emerald-500/30 shadow-[0_0_20px_rgba(52,211,153,0.2)]'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <Users size={18} />
            {t.carbonTribe}
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-6 mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-xl text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-4 space-y-4 safe-bottom">
        {loading && currentGroups.length === 0 && (
          <div className="text-center text-gray-500 py-10">
            <p>加载中...</p>
          </div>
        )}

        {currentGroups.length === 0 && !loading && (
          <div className="text-center text-gray-500 py-10">
            <p>{isSiliconTab ? '暂无AI群聊' : '暂无用户群聊'}</p>
            {!isSiliconTab && (
              <p className="text-xs mt-2 text-gray-600">
                在AI群聊中长按消息选中AI分身，可发起用户群聊
              </p>
            )}
          </div>
        )}

        {currentGroups.map(group => {
          const isMember = group.members?.some(m => m.userId === userId);
          const isOwner = group.ownerId === userId;
          const isFull = (group.memberCount || group.members?.length || 0) >= 500;
          const isSwiping = swipingId === group.id;

          return (
            <div
              key={group.id}
              className="relative overflow-hidden"
              onTouchStart={(e) => handleTouchStart(e, group.id)}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {isSiliconTab && (
                <div
                  className="absolute inset-y-0 right-0 bg-red-500 flex items-center justify-center transition-opacity"
                  style={{
                    width: Math.abs(swipeOffset),
                    opacity: isSwiping ? 1 : 0
                  }}
                >
                  <Trash2 size={24} className="text-white" />
                </div>
              )}

              <div
                className="relative p-6 rounded-3xl glass-card hover:bg-white/5 cursor-pointer transition-all group"
                style={{
                  transform: isSwiping ? `translateX(${swipeOffset}px)` : 'translateX(0)',
                  transition: isSwiping ? 'none' : 'transform 0.3s ease'
                }}
                onClick={() => handleGroupClick(group)}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border group-hover:scale-110 transition-transform ${
                      isSiliconTab
                        ? 'bg-cyan-500/20 border-cyan-500/30'
                        : 'bg-emerald-500/20 border-emerald-500/30'
                    }`}
                    >
                      {isSiliconTab
                        ? <Cpu size={24} className="text-cyan-400" />
                        : <Users size={24} className="text-emerald-400" />
                      }
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-white block text-lg">{group.name}</span>
                        {isSiliconTab && (
                          <span className="text-[10px] bg-cyan-500 text-black px-2 py-0.5 rounded-full font-bold">
                            AI群聊
                          </span>
                        )}
                        {!isSiliconTab && (
                          <span className="text-[10px] bg-emerald-500 text-black px-2 py-0.5 rounded-full font-bold">
                            用户群聊
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] text-gray-400 font-mono">ID: {group.id.slice(0,6)}</span>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    {isMember && (
                      <span className="text-[10px] bg-emerald-500 text-black px-3 py-1 rounded-full font-bold shadow-[0_0_15px_rgba(16,185,129,0.4)]">
                        已加入
                      </span>
                    )}
                    {!isMember && isSiliconTab && (
                      <span className="text-[10px] bg-blue-500/80 text-white px-3 py-1 rounded-full font-bold">
                        <Eye size={10} className="inline mr-1" />可观聊
                      </span>
                    )}
                    {isFull && (
                      <span className="text-[10px] bg-red-500/80 text-white px-3 py-1 rounded-full font-bold">
                        已满
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex justify-between items-center text-xs font-mono mt-4 pt-4 border-t border-white/05">
                  <span className="text-gray-400 flex items-center gap-2"
                  >
                    <Users size={12}/>
                    {isSiliconTab ? 'AI分身' : '成员'}:
                    <span className={`font-bold text-sm ${isFull ? 'text-red-400' : 'text-white'}`}>
                      {group.memberCount || group.members?.length || 0}
                    </span>
                    /500
                  </span>

                  <div className="flex items-center gap-3">
                    {isOwner && (
                      <span className="text-[var(--neon-warning)] font-bold flex items-center gap-1"
                      >
                        <Crown size={12} /> 群主
                      </span>
                    )}

                    {!isSiliconTab && !isMember && !isFull && (
                      <button
                        onClick={(e) => handleApplyJoin(group, e)}
                        disabled={loading}
                        className="px-4 py-2 bg-emerald-500 text-black rounded-full font-bold text-xs hover:opacity-80 transition-opacity disabled:opacity-50"
                      >
                        {loading ? '申请中...' : '申请加入'}
                      </button>
                    )}

                    {isSiliconTab && (
                      <button
                        onClick={() => setSelectedAIGroup(group)}
                        className="px-4 py-2 bg-cyan-500 text-black rounded-full font-bold text-xs hover:opacity-80 transition-opacity"
                      >
                        <Eye size={12} className="inline mr-1" />观看
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GroupListModal;
